import { ContractTypeAws, ContractTypeGoogleCloud } from "@doitintl/cmp-models";
import { Box, Button, TableCell, Typography } from "@mui/material";
import capitalize from "lodash/capitalize";
import { DateTime } from "luxon";

import Hide from "../../../Components/HideChildren/Hide";
import { useAuthContext } from "../../../Context/AuthContext";
import ContractsListItemMenu from "./ContractsListItemMenu";
import { StatusChip } from "./StatusChip";
import { type FormattedContract, type FormattedVendorContract } from "./types";
import { getContractEndDate, getVendorCommitmentText } from "./utils";

const VendorContractText = ({ children, show, dataCy }: { children: any; show?: boolean; dataCy?: string }) =>
  show ? (
    <Typography
      variant="caption"
      color="textSecondary"
      data-cy={dataCy}
      sx={{
        mb: 0,
        minHeight: "20px",
      }}
    >
      {children}
    </Typography>
  ) : null;

const ContractRow = ({
  contract,
  handleDownload,
  setOpenContractPage,
}: {
  contract: FormattedContract;
  handleDownload: (contract: FormattedContract | FormattedVendorContract) => Promise<void>;
  setOpenContractPage: (openContractPage: boolean) => void;
}) => {
  const { isDoitEmployee } = useAuthContext();
  const vendorCommitmentText = getVendorCommitmentText(contract);

  const vendorContract = contract.data.vendorContractData;

  const ifShowVendorContractRow =
    isDoitEmployee &&
    contract.data.isCommitment &&
    [ContractTypeGoogleCloud, ContractTypeAws].includes(contract.data.type);

  return (
    <>
      <TableCell>
        <Box data-cy={`vendor-type-${contract.id}`}>
          {contract.data._product}
          {!!contract.data._tier && ` - ${contract.data._tier}`}
        </Box>
        <VendorContractText show={ifShowVendorContractRow} dataCy={`vendor-commitment-${contract.id}`}>
          {vendorCommitmentText}
        </VendorContractText>
      </TableCell>

      <TableCell>
        <Box data-cy={`status-${contract.id}`}>
          <StatusChip status={contract.data._status} />
        </Box>
        <VendorContractText show={ifShowVendorContractRow} dataCy={`vendor-contract-status-${contract.id}`}>
          {capitalize(vendorContract?.data._status)}
        </VendorContractText>
      </TableCell>

      <TableCell>
        <Box data-cy={`start-date-${contract.id}`}>{contract?.data._startDate.toLocaleString(DateTime.DATE_MED)}</Box>
        <VendorContractText show={ifShowVendorContractRow} dataCy={`start-date-vendor-contract-${contract.id}`}>
          {vendorContract?.data._startDate?.toLocaleString(DateTime.DATE_MED)}
        </VendorContractText>
      </TableCell>

      <TableCell>
        <Box data-cy={`end-date-${contract.id}`}>{getContractEndDate(contract)}</Box>
        <VendorContractText show={ifShowVendorContractRow} dataCy={`end-date-vendor-contract-${contract.id}`}>
          {vendorContract?.data._endDate ? vendorContract?.data._endDate.toLocaleString(DateTime.DATE_MED) : ""}
        </VendorContractText>
      </TableCell>

      <Hide mdDown>
        <TableCell>
          <Box data-cy={`entity-${contract.id}`}>{contract.data._entity}</Box>
          <VendorContractText show={ifShowVendorContractRow} dataCy={`entity-vendor-contract-${contract.id}`}>
            {vendorContract?.data._entity}
          </VendorContractText>
        </TableCell>
      </Hide>

      <Hide smDown>
        <TableCell>
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Hide lgDown>
              <Button
                variant="outlined"
                onClick={() => {
                  setOpenContractPage(true);
                }}
                data-cy={`contract-quick-view-${contract.id}`}
              >
                Quick view
              </Button>
            </Hide>
            <ContractsListItemMenu
              contract={contract.data}
              id={contract.id}
              handleDownload={() => handleDownload(contract)}
              handleDownloadVendorContract={vendorContract ? () => handleDownload(vendorContract) : undefined}
            />
          </Box>
        </TableCell>
      </Hide>
    </>
  );
};

export default ContractRow;
